import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useWarehouseStore } from '@/stores/warehouse-store';
import {
  canAccessRoute,
  canCrossWarehouseMove,
  canPerformAction,
  ROUTE_PERMISSIONS,
  ACTION_PERMISSIONS,
} from '@/lib/permissions';
import type { RoutePermission, WarehouseRole } from '@/lib/permissions';
import { useRoutePermissions } from '@/api/hooks/useRoles';

/**
 * Hook that provides permission checks for the current user
 * at the currently selected warehouse.
 *
 * Uses API-backed permission data when available; falls back to the
 * bundled ROUTE_PERMISSIONS / ACTION_PERMISSIONS arrays on cold start.
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);

  const isAdmin = user?.isAdmin ?? false;
  const isAuditor = user?.isAuditor ?? false;

  // Fetch live permissions from the API (PR3: replace hardcoded checks).
  // Falls back to bundled constants if the query has not resolved yet.
  const { data: remotePermissions, isPending: remotePermissionsPending } =
    useRoutePermissions();

  // Effective permission arrays: prefer API data, fall back to bundled.
  // Legacy redirect paths are absent from both lists on purpose —
  // canAccessRoute short-circuits them via isLegacyRedirect before the lookup,
  // so they never reach "unknown route = deny".
  const effectiveRoutePermissions: RoutePermission[] =
    remotePermissions?.routes ?? ROUTE_PERMISSIONS;
  const effectiveActionPermissions =
    remotePermissions?.actions ?? ACTION_PERMISSIONS;

  // Stabilize the roles array reference so downstream useCallback/useMemo hooks
  // don't re-run when the user object updates but roles are unchanged.
  const warehouseRoles = useMemo<WarehouseRole[]>(() => {
    if (!selectedWarehouseId) return [];
    return (
      (user?.warehouseRoles?.[selectedWarehouseId] as WarehouseRole[]) ?? []
    );
  }, [selectedWarehouseId, user?.warehouseRoles]);

  const canAccess = useCallback(
    (routePath: string) =>
      canAccessRoute(
        warehouseRoles,
        isAdmin,
        isAuditor,
        routePath,
        effectiveRoutePermissions,
      ),
    [warehouseRoles, isAdmin, isAuditor, effectiveRoutePermissions],
  );

  const canPerform = useCallback(
    (action: string) =>
      canPerformAction(
        warehouseRoles,
        isAdmin,
        isAuditor,
        action,
        effectiveActionPermissions,
      ),
    [warehouseRoles, isAdmin, isAuditor, effectiveActionPermissions],
  );

  // canAccess, but for a warehouse other than the selected one.
  const canAccessAt = useCallback(
    (warehouseId: string, routePath: string) =>
      canAccessRoute(
        (user?.warehouseRoles?.[warehouseId] as WarehouseRole[]) ?? [],
        isAdmin,
        isAuditor,
        routePath,
        effectiveRoutePermissions,
      ),
    [user?.warehouseRoles, isAdmin, isAuditor, effectiveRoutePermissions],
  );

  // canPerform, but for a warehouse other than the selected one (e.g. before
  // switching to the warehouse an order lives in).
  const canPerformAt = useCallback(
    (warehouseId: string, action: string) =>
      canPerformAction(
        (user?.warehouseRoles?.[warehouseId] as WarehouseRole[]) ?? [],
        isAdmin,
        isAuditor,
        action,
        effectiveActionPermissions,
      ),
    [user?.warehouseRoles, isAdmin, isAuditor, effectiveActionPermissions],
  );

  // The LITERAL role at a named warehouse — no hierarchy expansion (nothing
  // implies WAREHOUSE_MANAGER), no admin/auditor bypass, false for a warehouse
  // the user has no roles in. For gates the API checks by role, not by action
  // (e.g. REPLACEMENT_GATE_ROLE), where a custom role granted the action is
  // not enough.
  const hasRoleAt = useCallback(
    (warehouseId: string, role: WarehouseRole) =>
      (user?.warehouseRoles?.[warehouseId] ?? []).includes(role),
    [user?.warehouseRoles],
  );

  // The warehouses where the user holds that literal role, in the order the
  // persisted `user.warehouseRoles` lists them. Read from the persisted user,
  // not the warehouse store's accessible list, which stays empty on a fresh
  // tab until /users/me returns while the roles are already there.
  const warehousesWithRole = useCallback(
    (role: WarehouseRole) =>
      Object.entries(user?.warehouseRoles ?? {})
        .filter(([, roles]) => roles.includes(role))
        .map(([warehouseId]) => warehouseId),
    [user?.warehouseRoles],
  );

  // Cross-WAREHOUSE move gate: exact INVENTORY_MANAGER at every involved
  // warehouse, or global admin. Deliberately NOT hierarchy-expanded — mirrors
  // the backend's canCrossWarehouseMove (see lib/permissions.ts).
  const canMoveCrossWarehouse = useCallback(
    (warehouseIds: string[]) =>
      canCrossWarehouseMove(
        user?.warehouseRoles as Record<string, WarehouseRole[]> | undefined,
        isAdmin,
        warehouseIds,
      ),
    [user?.warehouseRoles, isAdmin],
  );

  return {
    canAccess,
    canAccessAt,
    canPerform,
    canPerformAt,
    hasRoleAt,
    warehousesWithRole,
    /**
     * False until the API's permission list has loaded (or failed, leaving
     * the bundled fallback in charge). Until then canAccess/canPerform answer
     * from the bundled constants, so a one-shot decision should wait for this.
     */
    permissionsSettled: !remotePermissionsPending,
    canMoveCrossWarehouse,
    isAdmin,
    isAuditor,
    warehouseRoles,
  };
}
